import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../auth/AuthContext'
import { useDataPolicy, useRemoveDataPolicy, useSetDataPolicy } from '../api/rooms'
import { ApiError } from '../api/client'
import type { DataPolicyResponse } from '../api/types'
import { ChecklistTemplatesPanel } from '../components/ChecklistTemplatesPanel'
import { DATA_POLICY_DRAFT_TITLE, dataPolicyDraft } from '../lib/dataPolicyDraft'
import { errorMessage, formatUtc } from '../lib/format'

/** The first line of the starting text. While it is still there the text has not been reviewed, and is not published. */
const DRAFT_NOTICE = 'DRAFT FOR REVIEW BY COUNSEL'

/**
 * An organization, for its Owners: the privacy and data use policy that people accept before
 * they hand information over in any of its rooms (ADR 0008), the record of who accepted, and
 * the request-list templates its rooms are built from (ADR 0010).
 * The API decides who may be here: a non-member gets 404, staff who are not Owners 403.
 */
export function Organization() {
  const { organizationId = '' } = useParams()
  const { me } = useAuth()
  const organization = me?.organizations.find((o) => o.id === organizationId)
  const policy = useDataPolicy(organizationId)

  return (
    <section className="stack">
      <p className="small">
        <Link to="/">← Rooms</Link>
      </p>
      <h1 className="h1">{organization?.name ?? 'Organization'}</h1>

      {policy.isPending && <p className="muted">Loading…</p>}
      {policy.isError && (
        <div className="alert alert-error" role="alert">
          <p>
            {policy.error instanceof ApiError && policy.error.status === 403
              ? 'Only an owner of the organization can see and change its data policy.'
              : errorMessage(policy.error)}
          </p>
        </div>
      )}
      {policy.data && (
        <>
          <DataPolicyEditor
            organizationId={organizationId}
            organizationName={organization?.name ?? 'The organization'}
            policy={policy.data}
          />
          <DataPolicyRecord policy={policy.data} />
          <ChecklistTemplatesPanel organizationId={organizationId} />
        </>
      )}
    </section>
  )
}

const policySchema = z.object({
  title: z.string().trim().min(1, 'A title is required').max(200),
  body: z
    .string()
    .trim()
    .min(1, 'The policy text is required')
    .max(200_000)
    .refine((text) => !text.includes(DRAFT_NOTICE), {
      message:
        'This is still the starting draft. Have it reviewed, replace everything in [square brackets], and remove the draft notice before publishing.',
    }),
})
type PolicyForm = z.infer<typeof policySchema>

function DataPolicyEditor({
  organizationId,
  organizationName,
  policy,
}: {
  organizationId: string
  organizationName: string
  policy: DataPolicyResponse
}) {
  const set = useSetDataPolicy(organizationId)
  const remove = useRemoveDataPolicy(organizationId)
  const current = policy.current
  const [editing, setEditing] = useState(current === null)
  const form = useForm<PolicyForm>({
    resolver: zodResolver(policySchema),
    defaultValues: { title: current?.title ?? DATA_POLICY_DRAFT_TITLE, body: current?.body ?? '' },
  })

  return (
    <section className="panel">
      <h2 className="h2">Privacy and data use policy</h2>
      <p className="small muted">
        With a policy published, every participant of every room of {organizationName} must read and
        accept it before they upload a file or answer a request. They accept once for all rooms.
        Room owners are not asked. Changing the text is a new version, and everyone is asked again.
        Acceptances are kept as evidence even after the policy is taken down.
      </p>

      {current && !editing && (
        <div className="stack-sm">
          <p>
            <strong>{current.title}</strong>{' '}
            <span className="small muted">
              version {current.versionNumber}, published {formatUtc(current.setUtc)}
              {current.setBy ? ` by ${current.setBy}` : ''}
            </span>
          </p>
          <div className="nda-body">{current.body}</div>
          <div className="row-actions">
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Replace the text
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Take the policy down? Participants will no longer be asked to accept anything before uploading. The record of who accepted is kept.',
                  )
                )
                  remove.mutate(undefined, { onSuccess: () => setEditing(true) })
              }}
            >
              Take the policy down
            </button>
            {remove.isError && (
              <span className="error-text small">{errorMessage(remove.error)}</span>
            )}
          </div>
        </div>
      )}

      {editing && (
        <form
          className="form"
          onSubmit={form.handleSubmit((values) =>
            set.mutate(values, { onSuccess: () => setEditing(false) }),
          )}
        >
          {!current && (
            <p className="small muted">
              No policy is published, so nobody is asked to accept anything. Nothing turns this on
              but the Publish button below.
            </p>
          )}
          <label className="field">
            <span>Title</span>
            <input {...form.register('title')} autoComplete="off" />
            <span className="field-error">{form.formState.errors.title?.message}</span>
          </label>
          <label className="field">
            <span>Policy text</span>
            <textarea {...form.register('body')} rows={18} className="nda-textarea" />
            <span className="field-error">{form.formState.errors.body?.message}</span>
          </label>
          <div className="alert alert-info" role="note">
            <p className="small no-margin">
              <strong>
                The starting text is a draft for your counsel to review. It is not legal advice.
              </strong>{' '}
              It names no jurisdiction, and everything in [square brackets] is yours to fill in. It
              cannot be published until its draft notice is removed.
            </p>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={set.isPending}>
              {current ? 'Publish as a new version' : 'Publish the policy'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                if (
                  form.getValues('body').trim() === '' ||
                  window.confirm('Replace what is in the editor with the starting text?')
                )
                  form.setValue('body', dataPolicyDraft(organizationName), { shouldDirty: true })
              }}
            >
              Insert a starting text
            </button>
            {current && (
              <button type="button" className="btn" onClick={() => setEditing(false)}>
                Cancel
              </button>
            )}
            {set.isError && <span className="error-text small">{errorMessage(set.error)}</span>}
          </div>
        </form>
      )}
    </section>
  )
}

function DataPolicyRecord({ policy }: { policy: DataPolicyResponse }) {
  if (policy.versions.length === 0) return null

  return (
    <section className="panel">
      <h2 className="h2">Acceptance record</h2>
      {policy.versions.map((v) => (
        <div key={v.id} className="group">
          <h3 className="h3 no-margin">
            Version {v.versionNumber}: {v.title}{' '}
            {v.isCurrent ? (
              <span className="chip">current</span>
            ) : (
              <span className="chip chip-warn">retired</span>
            )}
          </h3>
          <p className="small muted">
            Published {formatUtc(v.createdUtc)}
            {v.setBy ? ` by ${v.setBy}` : ''}
            {v.retiredUtc ? `, retired ${formatUtc(v.retiredUtc)}` : ''} · {v.acceptanceCount}{' '}
            acceptance{v.acceptanceCount === 1 ? '' : 's'}
          </p>
          {v.acceptances.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Accepted</th>
                  <th>In room</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {v.acceptances.map((a) => (
                  <tr key={a.userId}>
                    <td>
                      {a.displayName ?? a.email ?? a.userId}
                      {a.displayName && a.email && <span className="small muted"> {a.email}</span>}
                    </td>
                    <td className="small">{formatUtc(a.acceptedUtc)}</td>
                    <td className="small">{a.roomName ?? '—'}</td>
                    <td className="small muted mono">{a.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </section>
  )
}
